/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
export enum LocationPermissions {
  /* Direct location access */

  /** No permissions granted */
  None = 0,

  /** Allowed at the location at which the user is part of the user group */
  CanWaste = 1,

  /** Allowed at the location at which the user is part of the user group */
  CanWitness = 2,

  /** Allowed at the location at which the user is part of the user group */
  CanViewHistoricalWastes = 4,

  /** Allowed at the location at which the user is part of the user group */
  CanEmptyBins = 8,

  /** Allowed at the location at which the user is part of the user group */
  CanManageCarts = 16,

  /* Children locations access only */

  /** Allowed only at the children locations at which the user is part of the user group */
  CanManageLocations = 32,

  /** Allowed only at the children locations at which the user is part of the user group */
  CanManageUserGroups = 64,

  /* Direct location access */

  /** Allowed at the location at which the user is part of the user group */
  CanResolve = 128,

  /** Allowed to transport the collected RxIntercepts to Central Pharmacy */
  CanTransport = 256,

  /** Allowed to final waste an RxIntercept in Central Pharmacy */
  CanFinalWaste = 512,

  /** Allowed to witness an RxIntercept being destroyed in Central Pharmacy as a final waste */
  CanWitnessFinalWaste = 1024,

  /** Allowed to witness transports */
  CanWitnessTransport = 2048,

  /** Allowed to view historical transports */
  CanViewHistoricalTransport = 4096,

  /** Allowed to resolve the transport records */
  CanResolveTransport = 8192,

  /** Allowed to view historical records of RxIntercepts destroyed in Central Pharmacy as a final waste */
  CanViewFinalWaste = 16384,

  /** Allowed to resolve the final waste records */
  CanResolveFinalWaste = 32768,

  /** Allowed to oversee fingerprint enrollment */
  CanEnrollFingerprints = 65536,

  /** Allowed to waste samples in BCBs after investigation */ 
  CanDestroyInvestigationSamples = 131072,

  /** Dummy Permission to allow ControlZone-only users */
  CanAccessControlZone = 1 << 18,

  /** Permission to access reports */
  CanViewReports = 1 << 19,

  /** Permission to access diagnostics on VIEW */
  CanAccessDiagnostics = 1 << 20,
}

export const PHARMACIST_PERMISSIONS = [
  LocationPermissions.CanViewHistoricalWastes,
  LocationPermissions.CanResolve,
  LocationPermissions.CanTransport,
  LocationPermissions.CanWitnessTransport,
  LocationPermissions.CanViewHistoricalTransport,
  LocationPermissions.CanResolveTransport,
  LocationPermissions.CanFinalWaste,
  LocationPermissions.CanWitnessFinalWaste,
  LocationPermissions.CanViewFinalWaste,
  LocationPermissions.CanResolveFinalWaste,
  LocationPermissions.CanAccessDiagnostics,
];

export const NURSE_PERMISSIONS = [
  LocationPermissions.CanWaste,
  LocationPermissions.CanWitness,
  LocationPermissions.CanViewHistoricalWastes,
];

export const PHARMACY_MANAGER_PERMISSIONS = [
  LocationPermissions.CanViewHistoricalWastes,
  LocationPermissions.CanResolve,
  LocationPermissions.CanTransport,
  LocationPermissions.CanWitnessTransport,
  LocationPermissions.CanViewHistoricalTransport,
  LocationPermissions.CanResolveTransport,
  LocationPermissions.CanFinalWaste,
  LocationPermissions.CanWitnessFinalWaste,
  LocationPermissions.CanViewFinalWaste,
  LocationPermissions.CanResolveFinalWaste,
  LocationPermissions.CanViewReports,
  LocationPermissions.CanEnrollFingerprints,
  LocationPermissions.CanAccessDiagnostics,
  LocationPermissions.CanManageUserGroups
];

export const PHARMACY_TECH_PERMISSIONS = [
  LocationPermissions.CanViewHistoricalWastes,
  LocationPermissions.CanTransport,
  LocationPermissions.CanWitnessTransport,
  LocationPermissions.CanViewHistoricalTransport,
  LocationPermissions.CanWitnessFinalWaste,
  LocationPermissions.CanViewFinalWaste,
];

export const NURSE_MANAGER_PERMISSIONS = [
  LocationPermissions.CanWaste,
  LocationPermissions.CanWitness,
  LocationPermissions.CanViewHistoricalWastes,
  LocationPermissions.CanViewReports,
  LocationPermissions.CanEnrollFingerprints,
  LocationPermissions.CanManageUserGroups,
  LocationPermissions.CanAccessDiagnostics
];

export const DIVERSION_COMMITTEE_PERMISSIONS = [
  LocationPermissions.CanViewHistoricalWastes,
  LocationPermissions.CanViewHistoricalTransport,
  LocationPermissions.CanFinalWaste,
  LocationPermissions.CanWitnessFinalWaste,
  LocationPermissions.CanViewFinalWaste,
  LocationPermissions.CanResolveFinalWaste,
  LocationPermissions.CanDestroyInvestigationSamples,
  LocationPermissions.CanAccessControlZone,
  LocationPermissions.CanViewReports,
  LocationPermissions.CanAccessDiagnostics,
];

export const ADMINISTRATOR_PERMISSIONS = [
  LocationPermissions.CanViewReports,
  LocationPermissions.CanManageUserGroups,
  LocationPermissions.CanEnrollFingerprints
];

export const SUPER_USER_PERMISSIONS = [
  LocationPermissions.CanWaste,
  LocationPermissions.CanWitness,
  LocationPermissions.CanViewHistoricalWastes,
  LocationPermissions.CanResolve,
  LocationPermissions.CanTransport,
  LocationPermissions.CanWitnessTransport,
  LocationPermissions.CanViewHistoricalTransport,
  LocationPermissions.CanResolveTransport,
  LocationPermissions.CanFinalWaste,
  LocationPermissions.CanWitnessFinalWaste,
  LocationPermissions.CanViewFinalWaste,
  LocationPermissions.CanResolveFinalWaste,
  LocationPermissions.CanDestroyInvestigationSamples,
  LocationPermissions.CanViewReports,
  LocationPermissions.CanManageUserGroups,
  LocationPermissions.CanEnrollFingerprints,
  LocationPermissions.CanAccessControlZone,
  LocationPermissions.CanAccessDiagnostics,
];

export const BUILDING_SERVICES_PERMISSIONS = [
  LocationPermissions.CanEmptyBins
];

export enum OrgRole {
  Administrator = 'Administrator',
  Nurse = 'Nurse',
  NurseManager = 'NurseManager',
  PharmacyTech = 'PharmacyTech',
  Pharmacist = 'Pharmacist',
  DiversionCommittee = 'DiversionCommittee',
  PharmacyManager = 'PharmacyManager',
  SuperUser = 'SuperUser',
}

/** Ordered lowest to highest privilege; a user resolves to the highest role whose permissions they fully hold. */
export const ROLE_HIERARCHY: readonly { role: OrgRole; permissions: LocationPermissions[] }[] = [
  { role: OrgRole.Administrator, permissions: ADMINISTRATOR_PERMISSIONS },
  { role: OrgRole.Nurse, permissions: NURSE_PERMISSIONS },
  { role: OrgRole.NurseManager, permissions: NURSE_MANAGER_PERMISSIONS },
  { role: OrgRole.PharmacyTech, permissions: PHARMACY_TECH_PERMISSIONS },
  { role: OrgRole.Pharmacist, permissions: PHARMACIST_PERMISSIONS },
  { role: OrgRole.DiversionCommittee, permissions: DIVERSION_COMMITTEE_PERMISSIONS },
  { role: OrgRole.PharmacyManager, permissions: PHARMACY_MANAGER_PERMISSIONS },
  { role: OrgRole.SuperUser, permissions: SUPER_USER_PERMISSIONS },
];